import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  sendSMS,
  getSMSTemplates,
} from '@/features/notifications/api';
import type { SendSMSRequest } from '@/features/notifications/api';
import { NotificationModel } from '@/features/notifications/notification';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ActionButton } from '@/components/ui/action-button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { parsePhone } from '@/lib/phone';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Bell,
  BellOff,
  CheckCheck,
  CreditCard,
  AlertTriangle,
  Lock,
  Unlock,
  ShieldCheck,
  Settings,
  FileText,
  RefreshCw,
  MessageSquare,
  Send,
  Filter,
} from '@/lib/icons';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ApiErrorCard } from '@/components/feedback/ApiErrorCard';
import { ButtonSpinner } from '@/components/feedback';
import { useToast } from '@/hooks/use-toast';
import {
  formatDateTime,
  formatDateTimeWithZone,
  formatRelative,
  formatTime,
  isToday,
  isYesterday,
} from '@/lib/dates';

export const Route = createFileRoute('/_layout/notifications/')({
  component: NotificationsPage,
});

const typeIcon = (type: string) => {
  const cls = 'w-5 h-5';
  switch (type) {
    case 'payment_received':
      return <CreditCard className={`${cls} text-emerald-500`} />;
    case 'payment_reminder':
      return <CreditCard className={`${cls} text-amber-500`} />;
    case 'payment_overdue':
      return <AlertTriangle className={`${cls} text-red-500`} />;
    case 'contract_activated':
      return <FileText className={`${cls} text-blue-500`} />;
    case 'device_locked':
      return <Lock className={`${cls} text-red-500`} />;
    case 'device_unlocked':
      return <Unlock className={`${cls} text-emerald-500`} />;
    case 'kyc_verified':
      return <ShieldCheck className={`${cls} text-violet-500`} />;
    case 'system':
      return <Settings className={`${cls} text-muted-foreground`} />;
    default:
      return <Bell className={`${cls} text-muted-foreground`} />;
  }
};

const typeBg = (type: string) => {
  switch (type) {
    case 'payment_received':
      return 'bg-emerald-500/10';
    case 'payment_reminder':
      return 'bg-amber-500/10';
    case 'payment_overdue':
      return 'bg-red-500/10';
    case 'contract_activated':
      return 'bg-blue-500/10';
    case 'device_locked':
      return 'bg-red-500/10';
    case 'device_unlocked':
      return 'bg-emerald-500/10';
    case 'kyc_verified':
      return 'bg-violet-500/10';
    case 'system':
      return 'bg-muted/60';
    default:
      return 'bg-muted/60';
  }
};

const typeLabel = (type: string) => {
  switch (type) {
    case 'payment_received':
      return 'Payment Received';
    case 'payment_reminder':
      return 'Payment Reminder';
    case 'payment_overdue':
      return 'Payment Overdue';
    case 'contract_activated':
      return 'Contract Activated';
    case 'device_locked':
      return 'Device Locked';
    case 'device_unlocked':
      return 'Device Unlocked';
    case 'kyc_verified':
      return 'KYC Verified';
    case 'system':
      return 'System';
    default:
      return type;
  }
};

// ── Type Filter Options ─────────────────────────────────────────

const filterOptions = [
  { value: 'all', label: 'All Types' },
  { value: 'payment_received', label: 'Payment Received' },
  { value: 'payment_reminder', label: 'Payment Reminder' },
  { value: 'payment_overdue', label: 'Payment Overdue' },
  { value: 'contract_activated', label: 'Contract Activated' },
  { value: 'device_locked', label: 'Device Locked' },
  { value: 'device_unlocked', label: 'Device Unlocked' },
  { value: 'kyc_verified', label: 'KYC Verified' },
  { value: 'system', label: 'System' },
];

// ── SMS Compose Dialog ──────────────────────────────────────────

function SMSComposeDialog() {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState('');
  const [template, setTemplate] = useState('payment_reminder');
  const [locale, setLocale] = useState<'en' | 'so'>('en');
  const [params, setParams] = useState('');

  const { data: templatesResult } = useQuery({
    queryKey: ['sms-templates'],
    queryFn: getSMSTemplates,
    enabled: open,
    staleTime: 5 * 60_000,
  });

  const sendMutation = useMutation({
    mutationFn: (data: SendSMSRequest) => sendSMS(data),
    onSuccess: (result) => {
      if (result.succeeded) {
        toast({
          title: '✅ SMS Sent',
          description: `SMS sent successfully to ${phone}`,
        });
        setOpen(false);
        setPhone('');
        setParams('');
      } else {
        toast({
          title: 'Failed',
          description: result.errors?.join(', ') ?? 'Failed to send SMS',
          variant: 'destructive',
        });
      }
    },
    onError: () =>
      toast({
        title: 'Error',
        description: 'Failed to send SMS',
        variant: 'destructive',
      }),
  });

  const recipient = parsePhone(phone).e164;

  const handleSend = () => {
    if (!recipient) return;
    let parsedParams: Record<string, string> = {};
    if (params.trim()) {
      try {
        parsedParams = JSON.parse(params);
      } catch {
        toast({
          title: 'Invalid JSON',
          description: 'Params must be valid JSON (e.g. {"amount": "15.00"})',
          variant: 'destructive',
        });
        return;
      }
    }
    sendMutation.mutate({
      recipientPhone: recipient,
      template,
      locale,
      params: parsedParams,
    });
  };

  const templates = templatesResult?.value ?? [];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="default"
          size="sm"
          className="rounded-xl bg-primary hover:bg-primary/90"
        >
          <MessageSquare className="w-4 h-4 mr-2" />
          Send SMS
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">
            Send SMS Notification
          </DialogTitle>
          <DialogDescription>
            Send an SMS to a customer via the notification service.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="sms-phone">Recipient Phone</Label>
            <PhoneInput
              id="sms-phone"
              value={phone}
              onChange={setPhone}
              className="rounded-xl"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Template</Label>
              <Select value={template} onValueChange={setTemplate}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {templates.length > 0
                    ? templates.map((t) => (
                        <SelectItem key={t.key} value={t.key}>
                          {t.key.replace(/_/g, ' ')}
                        </SelectItem>
                      ))
                    : [
                        'payment_reminder',
                        'payment_received',
                        'overdue_warning',
                        'contract_activated',
                        'device_locked',
                        'welcome',
                      ].map((k) => (
                        <SelectItem key={k} value={k}>
                          {k.replace(/_/g, ' ')}
                        </SelectItem>
                      ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Language</Label>
              <Select
                value={locale}
                onValueChange={(v) => setLocale(v as 'en' | 'so')}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">🇺🇸 English</SelectItem>
                  <SelectItem value="so">🇸🇴 Soomaali</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="sms-params">
              Template Params{' '}
              <span className="text-muted-foreground text-xs">
                (JSON, optional)
              </span>
            </Label>
            <Input
              id="sms-params"
              placeholder='{"amount": "15.00", "currency": "USD"}'
              value={params}
              onChange={(e) => setParams(e.target.value)}
              className="rounded-xl font-mono text-xs"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setOpen(false)}
            className="rounded-xl"
          >
            Cancel
          </Button>
          <ActionButton
            onClick={handleSend}
            disabled={!recipient}
            loading={sendMutation.isPending}
            loadingText="Sending..."
            icon={<Send className="h-4 w-4" />}
            className="rounded-xl bg-primary hover:bg-primary/90"
          >
            Send SMS
          </ActionButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main Page ───────────────────────────────────────────────────

function NotificationsPage() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [typeFilter, setTypeFilter] = useState('all');

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => getNotifications(),
    retry: 1,
    staleTime: 30_000,
  });

  const markRead = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['notifications'] }),
    onError: () =>
      toast({
        title: 'Error',
        description: 'Failed to mark notification as read',
        variant: 'destructive',
      }),
  });

  const markAllRead = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      toast({ title: 'Done', description: 'All notifications marked as read' });
    },
    onError: () =>
      toast({
        title: 'Error',
        description: 'Failed to mark all as read',
        variant: 'destructive',
      }),
  });

  const allNotifications = useMemo(() => data?.value ?? [], [data]);
  const unreadCount = useMemo(
    () => allNotifications.filter((n: NotificationModel) => !n.isRead).length,
    [allNotifications]
  );

  // Memoized stats to avoid inline filters on every render
  const stats = useMemo(
    () => ({
      payments: allNotifications.filter((n: NotificationModel) =>
        n.type.startsWith('payment')
      ).length,
      alerts: allNotifications.filter((n: NotificationModel) =>
        ['device_locked', 'payment_overdue'].includes(n.type)
      ).length,
    }),
    [allNotifications]
  );

  // Filtered notifications
  const notifications = useMemo(() => {
    if (typeFilter === 'all') return allNotifications;
    return allNotifications.filter(
      (n: NotificationModel) => n.type === typeFilter
    );
  }, [allNotifications, typeFilter]);

  const sentLabel = (sentAt: string) => {
    if (isToday(sentAt)) return formatRelative(sentAt, i18n.language);
    if (isYesterday(sentAt)) {
      return t('common.dates.yesterdayAt', { time: formatTime(sentAt) });
    }
    return formatDateTime(sentAt);
  };

  const groupByDate = (items: NotificationModel[]) => {
    const groups: { label: string; items: NotificationModel[] }[] = [];

    const todayItems: NotificationModel[] = [];
    const yesterdayItems: NotificationModel[] = [];
    const earlierItems: NotificationModel[] = [];

    items.forEach((n) => {
      if (isToday(n.sentAt)) todayItems.push(n);
      else if (isYesterday(n.sentAt)) yesterdayItems.push(n);
      else earlierItems.push(n);
    });

    if (todayItems.length > 0)
      groups.push({ label: t('common.dates.today'), items: todayItems });
    if (yesterdayItems.length > 0)
      groups.push({ label: t('common.dates.yesterday'), items: yesterdayItems });
    if (earlierItems.length > 0)
      groups.push({ label: t('common.dates.earlier'), items: earlierItems });
    return groups;
  };

  const grouped = groupByDate(notifications);

  // Navigate to related resource on notification click
  const handleNotificationClick = (n: NotificationModel) => {
    if (!n.isRead && !markRead.isPending) {
      markRead.mutate(n.id);
    }
    // Navigate based on type
    if (n.contractId) {
      navigate({ to: `/contracts/${n.contractId}` });
    } else if (n.type.startsWith('payment')) {
      navigate({ to: '/payments' });
    } else if (n.type.startsWith('device')) {
      navigate({ to: '/inventory' });
    }
  };

  return (
    <div className="space-y-8">
      {/* Header — asymmetric spacing per DESIGN.md */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
        <div>
          <h1 className="text-2xl font-bold tracking-tight font-display">
            Notifications
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Stay up to date with payments, device events, and system alerts
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {unreadCount > 0 && (
            <Badge className="bg-primary/10 text-primary border-0 text-sm px-3 py-1 rounded-full">
              {unreadCount} unread
            </Badge>
          )}

          {/* Type Filter */}
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[160px] rounded-xl h-9 text-sm">
              <Filter className="w-3.5 h-3.5 mr-1.5 text-muted-foreground" />
              <SelectValue placeholder="Filter..." />
            </SelectTrigger>
            <SelectContent>
              {filterOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <SMSComposeDialog />

          <ActionButton
            variant="outline"
            size="sm"
            className="rounded-xl"
            onClick={() => markAllRead.mutate()}
            disabled={unreadCount === 0}
            loading={markAllRead.isPending}
            loadingText="Marking..."
            icon={<CheckCheck className="h-4 w-4" />}
          >
            Mark All Read
          </ActionButton>
          <Button
            variant="ghost"
            size="icon"
            className="rounded-xl h-9 w-9"
            onClick={() => refetch()}
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Summary Stats — border-0 + surface hierarchy per DESIGN.md */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          {
            label: 'Total',
            value: allNotifications.length,
            icon: Bell,
            color: 'from-blue-500 to-blue-600',
          },
          {
            label: 'Unread',
            value: unreadCount,
            icon: Bell,
            color: 'from-amber-500 to-amber-600',
          },
          {
            label: 'Payments',
            value: stats.payments,
            icon: CreditCard,
            color: 'from-emerald-500 to-emerald-600',
          },
          {
            label: 'Alerts',
            value: stats.alerts,
            icon: AlertTriangle,
            color: 'from-red-500 to-red-600',
          },
        ].map((s) => (
          <Card
            key={s.label}
            className="relative overflow-hidden border-0 shadow-[0_4px_20px_-4px_hsl(213_20%_15%/0.04)] rounded-xl"
          >
            <div
              className={`absolute inset-0 bg-gradient-to-br ${s.color} opacity-[0.05]`}
            />
            <CardContent className="p-4 flex items-center gap-3 relative">
              <div
                className={`p-2.5 rounded-xl bg-gradient-to-br ${s.color} text-white shadow-lg`}
              >
                <s.icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-2xl font-bold tracking-tight">
                  {isLoading ? '—' : s.value}
                </p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Notification List — No divider lines, use spacing per DESIGN.md */}
      <Card className="shadow-[0_4px_20px_-4px_hsl(213_20%_15%/0.04)] border-0 rounded-xl overflow-hidden">
        <CardHeader className="pb-3 bg-surface-container-low/50">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-display">
              {typeFilter === 'all'
                ? 'All Notifications'
                : `${filterOptions.find((o) => o.value === typeFilter)?.label ?? ''} Notifications`}
            </CardTitle>
            <span className="text-xs text-muted-foreground">
              {notifications.length} result
              {notifications.length !== 1 ? 's' : ''}
            </span>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-start gap-4 p-3">
                  <Skeleton className="h-10 w-10 rounded-xl shrink-0" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-1/3" />
                    <Skeleton className="h-3 w-2/3" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                </div>
              ))}
            </div>
          ) : isError ? (
            <div className="p-6">
              <ApiErrorCard
                error={error}
                onRetry={() => refetch()}
                title="Failed to load notifications"
              />
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-16 text-center text-muted-foreground">
              <BellOff className="w-12 h-12 mx-auto mb-4 opacity-20" />
              <p className="font-medium text-base">
                {typeFilter === 'all'
                  ? 'No notifications'
                  : 'No matching notifications'}
              </p>
              <p className="text-sm mt-1">
                {typeFilter === 'all'
                  ? "You're all caught up!"
                  : 'Try selecting a different filter.'}
              </p>
            </div>
          ) : (
            <div>
              {grouped.map((group) => (
                <div key={group.label}>
                  {/* Group header — subtle background shift instead of border */}
                  <div className="sticky top-0 z-10 bg-surface-container-low/80 backdrop-blur-sm px-5 py-2.5 flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {group.label}
                    </span>
                    <span className="text-[10px] font-medium text-muted-foreground/60 bg-muted/40 px-2 py-0.5 rounded-full">
                      {group.items.length}
                    </span>
                  </div>
                  {/* Items — spacing instead of dividers per DESIGN.md */}
                  <div className="px-3 py-1 space-y-1">
                    {group.items.map((n: NotificationModel) => {
                      const isMarkingRead =
                        markRead.isPending && markRead.variables === n.id;
                      return (
                        <div
                          key={n.id}
                          className={`flex items-start gap-4 p-4 rounded-xl transition-all duration-200 cursor-pointer relative ${
                            !n.isRead
                              ? 'bg-primary/[0.04] hover:bg-primary/[0.07]'
                              : 'hover:bg-surface-container-low/60'
                          } ${isMarkingRead ? 'opacity-50' : ''}`}
                          onClick={() => handleNotificationClick(n)}
                        >
                          {/* Unread accent bar */}
                          {!n.isRead && (
                            <div className="absolute inset-y-2 left-1 w-[3px] bg-primary rounded-full" />
                          )}
                          <div
                            className={`p-2.5 rounded-xl ${typeBg(n.type)} shrink-0`}
                          >
                            {isMarkingRead ? (
                              <ButtonSpinner />
                            ) : (
                              typeIcon(n.type)
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span
                                className={`font-medium text-sm ${
                                  !n.isRead
                                    ? 'text-foreground'
                                    : 'text-muted-foreground'
                                }`}
                              >
                                {n.title || typeLabel(n.type)}
                              </span>
                              {!n.isRead && !isMarkingRead && (
                                <span className="w-2 h-2 rounded-full bg-primary shrink-0" />
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">
                              {n.message}
                            </p>
                            <p
                              className="text-xs text-muted-foreground/50 mt-1.5"
                              title={formatDateTimeWithZone(n.sentAt)}
                            >
                              {sentLabel(n.sentAt)}
                            </p>
                          </div>
                          {n.contractId && (
                            <span className="text-[10px] text-primary/60 font-medium mt-1 shrink-0">
                              View →
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      {/* SMS Templates */}
      <Card className="shadow-md border-0">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-gradient-to-br from-violet-500 to-violet-600 text-white shadow-lg">
                <MessageSquare className="h-4 w-4" />
              </div>
              <div>
                <CardTitle className="text-lg">SMS Templates</CardTitle>
                <p className="text-xs text-muted-foreground mt-0.5">
                  BNPL lifecycle notification templates
                </p>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[
              {
                event: 'Welcome',
                trigger: 'Contract activated',
                template: 'Welcome to Nuur Mobile! Your device financing is active. Your first payment of $X is due on [date].',
                channel: 'SMS',
                color: 'bg-emerald-500/10 border-emerald-500/20',
              },
              {
                event: 'ZAAD Deduction Success',
                trigger: 'Successful auto-deduction',
                template: 'Payment of $X received via ZAAD. Your balance is now $Y. Next payment due [date].',
                channel: 'SMS',
                color: 'bg-emerald-500/10 border-emerald-500/20',
              },
              {
                event: 'Partial Deduction',
                trigger: 'Partial amount collected',
                template: 'Partial payment of $X received. Remaining $Y still due. Please top up your wallet.',
                channel: 'SMS',
                color: 'bg-amber-500/10 border-amber-500/20',
              },
              {
                event: 'Deduction Failed',
                trigger: 'ZAAD deduction fails',
                template: 'We could not process your installment of $X. Please top up your ZAAD wallet to avoid restriction.',
                channel: 'SMS',
                color: 'bg-red-500/10 border-red-500/20',
              },
              {
                event: 'Grace Period Warning',
                trigger: 'Grace period start',
                template: 'Your payment is overdue. You have [N] days grace period remaining before device restriction.',
                channel: 'SMS',
                color: 'bg-orange-500/10 border-orange-500/20',
              },
              {
                event: 'Final Warning',
                trigger: 'Grace period expiring',
                template: 'FINAL NOTICE: Your device will be restricted tomorrow unless payment of $X is received.',
                channel: 'SMS',
                color: 'bg-red-500/10 border-red-500/20',
              },
              {
                event: 'Device Restricted',
                trigger: 'Lock applied',
                template: 'Your device has been restricted due to overdue payment of $X. Pay now to restore access.',
                channel: 'SMS',
                color: 'bg-red-500/10 border-red-500/20',
              },
              {
                event: 'Device Restored',
                trigger: 'Unlock after payment',
                template: 'Payment received! Your device has been restored. Thank you for your payment.',
                channel: 'SMS',
                color: 'bg-emerald-500/10 border-emerald-500/20',
              },
              {
                event: 'Offline Cash Received',
                trigger: 'Agent records cash',
                template: 'Cash payment of $X received by agent [name]. Your balance is now $Y.',
                channel: 'SMS',
                color: 'bg-blue-500/10 border-blue-500/20',
              },
              {
                event: 'Overpayment',
                trigger: 'Amount exceeds due',
                template: 'You paid $X which exceeds your due amount. The excess $Y has been applied to future installments.',
                channel: 'SMS',
                color: 'bg-violet-500/10 border-violet-500/20',
              },
              {
                event: 'Contract Complete',
                trigger: 'Final payment made',
                template: 'Congratulations! Your financing is fully paid. Your device is now fully yours. Thank you!',
                channel: 'SMS',
                color: 'bg-emerald-500/10 border-emerald-500/20',
              },
              {
                event: 'Dispute Logged',
                trigger: 'Customer complaint',
                template: 'Your dispute has been logged (Ref: [ref]). Our team will review and respond within [SLA] hours.',
                channel: 'SMS',
                color: 'bg-amber-500/10 border-amber-500/20',
              },
            ].map((tpl) => (
              <div
                key={tpl.event}
                className={`rounded-xl border p-4 ${tpl.color} transition-all duration-200 hover:shadow-sm`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold">{tpl.event}</span>
                  <Badge variant="outline" className="text-[10px]">
                    {tpl.channel}
                  </Badge>
                </div>
                <p className="text-[10px] text-muted-foreground mb-2">
                  Trigger: {tpl.trigger}
                </p>
                <p className="text-xs italic text-muted-foreground leading-relaxed">
                  "{tpl.template}"
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
